/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.


import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';


/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  EXECUTE: "execute",
  STOP: "stop",
  COMMON_ERROR: "commonError",
  ERROR: "error"
};

/** API type定義 */
const API_TYPE = {
  START: 'start',
  SEC_ACCESS: 'sec_access',
  EXECUTE: 'execute',
  TERMINATE: 'terminate',
  DISPOSE: 'dispose'
};

/** 現在セッション判定 */
const IS_SESSION = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: "requirement119",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isSession: IS_SESSION.DEFAULT,
      modal_requirement_119: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.COMMON_ERROR:
        case PHASE.ERROR:
          return "danger";
        case PHASE.START:
        case PHASE.EXECUTE:
        case PHASE.STOP:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_119 = true;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.COMMON_ERROR;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_119 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** エラー画面 セッション遷移判定 */
    async showError() {
      if (this.isSession === IS_SESSION.NON_DEFAULT) {
        // 現在セッションがNonDefaultSessionであればterminateAPI実行
        await this.doTerminate();
      } else {
        // 終了処理実行
        this.endProcessing();
      }
    },

    /** 終了処理を実施する */
    async endProcessing() {
      // dispose実施
      await this.doDispose();

      // 機能終了
      this.closeWizard();

      // 初期設定に戻す
      this.phase = PHASE.START;
      this.isSession = IS_SESSION.DEFAULT;
    },

    // API実行 -------------------------------------------

    /** start */
    async doStart() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.START,
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              await this.doSecAccess();
              break;
            case config.STATUS_WSP_FAILED:
              // 各エラー表示
              switch (response.data.async_code) {
                case error.WSP_119_PRIVATE_ERROR:
                  this.isSession = IS_SESSION.DEFAULT;
                  // エラー画面に遷移
                  this.phase = PHASE.ERROR;
                  break;
                default:
                  // 該当のエラーコードが存在しない場合、共通エラー
                  // ありえないが定義しておく
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** セキュリティアクセス判定 */
    async doSecAccess() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.SEC_ACCESS
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 実行
              this.phase = PHASE.EXECUTE;
              await this.doExecute();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 共通エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** execute */
    async doExecute() {
      try {
        this.isLoading = true;

        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.EXECUTE,
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.phase = PHASE.STOP;
              break;
            case config.STATUS_WSP_FAILED:
              // 失敗
              // 各エラー表示
              switch (response.data.async_code) {
                case error.WSP_119_PRIVATE_ERROR:
                  this.isSession = IS_SESSION.NON_DEFAULT;
                  // エラー画面に遷移
                  this.phase = PHASE.ERROR;
                  break;
                default:
                  // 該当のエラーコードが存在しない場合、共通エラー
                  // ありえないが定義しておく
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** terminate */
    async doTerminate() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE,
        };

        // 初期処理（要求）
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;

        // 終了処理実行
        this.endProcessing();
      }
    },

    /** 終了処理 */
    async doDispose() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.DISPOSE
        };

        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

      } finally {
        this.isLoading = false;
      }
    },

    // ユーザ操作 -------------------------------------------

    /** 待機画面にて駆動押下時の処理 */
    onDriveClicked() {
      // startAPI実行
      this.doStart();
    },

    /** 待機画面にて終了押下時の処理 */
    onTerminateClicked() {
      // 画面を閉じる
      this.closeWizard();
    },

    /** 停止画面にてOK押下時の処理 */
    onStopOkClicked() {
      // terminateAPI実行
      this.doTerminate();
    },

    /** エラー画面にてOK押下時の処理 */
    onErrorOkClicked() {
      // terminateAPI実行
      this.showError();
    },

    /** 共通エラーにてOK押下時の処理 */
    onCmnErrOkClicked() {
      // terminateAPI実行
      this.doTerminate();
    }
  },

  async mounted() {
    this.init();
  }
};
